/* eslint-disable new-cap */
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import {LitElement, html, nothing} from 'lit';
import {createRef, ref} from 'lit/directives/ref.js';
import styles from './facet-list.component.scss?as=stylesheet';
import {defineCustomElement} from 'extensions/utils';
import {classMap} from 'lit/directives/class-map.js';

const SHOWN_HIDDEN_FACETS_INDEX = 5;

class FacetList extends MobxReactionUpdate(LitElement) {

	static get styles () {
		return [styles];
	}

	static get properties () {
		return {
			apiEndpoint: {
				attribute: 'api-endpoint',
				type: String
			},
			facets: {
				type: Array
			},
			screenSize: {
				attribute: 'screen-size',
				type: String
			},
			selectedFacets: {
				type: Array
			},
			service: {
				state: true,
				type: Object
			},
			store: {
				state: true,
				type: Object
			}
		};
	}

	constructor () {
		super();
		this.modal = createRef();

		/**
		 * @type {string}
		 */
		this.apiEndpoint = '';

		/**
		 * @type {import('extensions/hawksearch/services/types').Facet[]}
		 */
		this.facets = [];

		/**
		 * @type {string}
		 */
		this.screenSize = '';

		/**
		 * @type {import('extensions/hawksearch/services/types').SelectedFacet}
		 */
		this.selectedFacets = [];

		/**
		 * @type {import('extensions/hawksearch/services/search-service').SearchService}
		 */
		this.service = {};

		/**
		 * @type {import('extensions/ring-builder/data/ring-builder-store').RingBuilderStore}
		 */
		this.store = {};
	}

	handleResetFacets () {
		return this.service.clearFacets();
	}

	renderMobileFilters () {
		const facetClasses = {
			'facet-list---inner': true,
			'facet-list---inner--loading': this.service?.store?.state?.results?.length && this.service?.store?.loading
		};

		return html`
			<div class="facets-toggle">
				<div class="facets-toggle--left">
					<div class="facets-toggle--heading">Customize Your Search</div>
					<span class="facets-toggle--mobile-reset">${this.renderReset()}</span>
				</div>
				<g-modal ${ref(this.modal)} lock="true" direction="right" animation="slide" header="false" content-padding="false" class="facet-list-modal">
					<button slot="trigger" class="facets-toggle--button">Filters</button>
					<div slot="content" class="${classMap(facetClasses)}">
						${this.renderFacetList()}
						<div class="rb-filter-footer">
							<button @click="${() => this.modal?.value?.close()}" class="rb-facet-close-bttn">Apply Filters</button>
						</div>
					</div>
				</g-modal>
			</div>
			${this.renderLabGrownFacet()}
		`;
	}

	renderDesktopFilters () {
		return html`
			${this.renderLabGrownFacet()}
			${this.renderFacetList()}
		`;
	}

	renderFacetList () {
		return html`
			<div class="facets">
				<div class="facets-header">
					<h4 class="facets-header--title">Customize Your Search</h4>
					${this.renderReset()}
				</div>
				${this.renderFacetsMiddle()}
				<div class="facets--filters">
					${this.renderFacets()}
				</div>
			</div>
		`;
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').Facet} facet
	 */
	renderFacet (facet) {
		switch (facet?.type) {
			case 'range-slider':
				return html`<rb-facet-range-slider .facet="${facet}" api-endpoint="${this.apiEndpoint}" .service="${this.service}"></rb-facet-range-slider>`;
			case 'step-slider':
				return html`<rb-facet-step-slider .facet="${facet}" api-endpoint="${this.apiEndpoint}" .service="${this.service}"></rb-facet-step-slider>`;
			case 'setting-metal':
				return html`<rb-facet-setting-metal .facet="${facet}" api-endpoint="${this.apiEndpoint}" .service="${this.service}"></rb-facet-setting-metal>`;
			case 'setting-style':
				return html`<rb-facet-setting-style .facet="${facet}" api-endpoint="${this.apiEndpoint}" .service="${this.service}"></rb-facet-setting-style>`;
			case 'diamond-shape':
				return html`<rb-facet-diamond-shape .facet="${facet}" api-endpoint="${this.apiEndpoint}" .service="${this.service}"></rb-facet-diamond-shape>`;
			case 'toggle':
				return html`<rb-facet-toggle .facet="${facet}" api-endpoint="${this.apiEndpoint}" .service="${this.service}"></rb-facet-toggle>`;
			default:
				return html`<rb-facet-basic .facet="${facet}" api-endpoint="${this.apiEndpoint}" .service="${this.service}"></rb-facet-basic>`;
		}
	}

	renderFacets () {
		// Exit if the length is less than the split index
		if (this.facets?.length <= SHOWN_HIDDEN_FACETS_INDEX) {
			return this.facets?.map(facet => this.renderFacet(facet));
		}

		// Split array into two different arrays and split into show-hide component
		const shown = this.facets?.slice(0, SHOWN_HIDDEN_FACETS_INDEX) ?? [];
		const hidden = this.facets?.slice(SHOWN_HIDDEN_FACETS_INDEX) ?? [];

		return html`
			${shown.map(facet => this.renderFacet(facet))}
			<rb-show-hide scroll-after-toggle shown-label="Hide More Filters" hidden-label="Show More Filters" hidden>
				${hidden.map(facet => this.renderFacet(facet))}
			</rb-show-hide>
		`;
	}

	renderFacetsMiddle () {
		return html`
			<div class="facets-middle">
				<div class="facets-middle--left">
					${this.renderReset()}
				</div>
				<div class="facets-middle--right">
					<rb-chat-widget></rb-chat-widget>
				</div>
			</div>
		`;
	}

	renderReset () {
		if (this.selectedFacets?.length === 0) {
			return nothing;
		}

		return html`
			<button class="facets-reset" @click="${this.handleResetFacets}" aria-label="Reset Filters">
				<span class="icon-reset facet-reset--icon" aria-hidden="true"></span>
				<span class="facets-reset--text">Reset Filters</span>
			</button>
		`;
	}

	renderLabGrownFacet () {
		if (!this.service?.store?.state?.custom?.labGrownFacet) {
			return nothing;
		}

		return html`<rb-facet-diamond-labgrown .store="${this.store}" .facet="${this.service?.store?.state?.custom?.labGrownFacet}" api-endpoint="${this.apiEndpoint}"></rb-facet-diamond-labgrown>`;
	}

	render () {
		return this.screenSize === 'desktop'
			? this.renderDesktopFilters()
			: this.renderMobileFilters();
	}

}

defineCustomElement('rb-facet-list', FacetList);
